import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { queryCollegeTeamsAPI } from '../../../../actions/college-corner.actions';
import useCppCollegeTeamsAccess from '../../../../components/hooks/useCppCollegeTeamsAccess';
import { useStatsig } from '../../../../components/hooks/useStatsig';
import Table from '../../../../components/Table';
import { parseQueryParams, setQueryParams } from '../../../../utils/collegeCorner';
import { POWER6_KEYS } from '../../../../utils/constants';
import CollegeCornerFilters from '../../filters/CollegeCornerFilters';
import config from '../../../../../config';
import { initialState } from '../helpers';
import styles from './CollegeCornerCollegeTeams.scss';
import CppCollegeTeams from './CppCollegeTeams';
import { getColumns, getInitialFilters } from './helpers';

function LegacyCollegeTeams({ history, location, isPowerUser, hideMaskedP6, isRostersRolloutEnabled = false }) {
    const dispatch = useDispatch();
    const { checkGate, featureGates } = useStatsig();
    const [data, setData] = useState(initialState);
    const [filters, setFilters] = useState(null);
    const [tempFilters, setTempFilters] = useState({});
    const callCollegeTeamsAPI = ({ ...params }) => dispatch(queryCollegeTeamsAPI({ ...params }));

    const clubRedirectBaseUrl = checkGate(featureGates.CLUB_LINKS_REDIRECT)
        ? `${config.nextAppHost}/college`
        : null;
    const columns = useMemo(
        () => getColumns(isRostersRolloutEnabled, clubRedirectBaseUrl),
        [isRostersRolloutEnabled, clubRedirectBaseUrl],
    );
    const initialFilters = useMemo(() => getInitialFilters(isRostersRolloutEnabled), [isRostersRolloutEnabled]);

    useEffect(() => {
        setFilters({ ...initialFilters, sort: isPowerUser ? initialFilters.sort : 'name', ...parseQueryParams(location.search) });
    }, [initialFilters, isPowerUser]);

    useEffect(() => {
        if (filters) {
            const query = setQueryParams(filters);
            history.replace(`${location.pathname}?${query}`);
        }
    }, [filters]);

    const onAPILoad = async (apiData, reset = false) => {
        if (reset) {
            setData(initialState);
        }

        const response = await callCollegeTeamsAPI(apiData);
        const { rows, count } = response;
        if (rows) {
            setData(() => ({
                rows,
                count,
                isLoad: false,
            }));
        }
    };

    const onhandleSort = (sortValue) => {
        if (sortValue && sortValue === filters.sort) {
            setFilters((prevState) => ({
                ...prevState,
                order: filters.order === 'asc' ? 'desc' : 'asc',
                page: 1,
            }));
        } else {
            setFilters((prevState) => ({
                ...prevState,
                sort: sortValue,
                order: 'desc',
                page: 1,
            }));
        }
    };

    const onLoadMore = () => {
        setFilters((prevState) => ({
            ...prevState,
            page: prevState.page + 1,
        }));
    };

    const isPropsValid = (props) => {
        return props
            ? Object.keys(props).filter((v) => Object.keys(filters).indexOf(v) > -1)
                .length > 0
            : {};
    };

    const onhandleUpdateFilters = (updatedFilters) => {
        const updatedProps = isPropsValid(updatedFilters) ? updatedFilters : {};
        setTempFilters((prevState) => ({
            ...prevState,
            ...updatedProps,
        }));
    };

    const onhandleFilterSubmit = (props = {}) => {
        const updatedProps = isPropsValid(props) ? props : {};
        setFilters((prevState) => ({
            ...prevState,
            ...tempFilters,
            ...updatedProps,
            page: 1,
        }));
        setTempFilters({});
    };

    const onhandleResetFilters = () => {
        setTempFilters({});
        setFilters({ ...initialFilters, sort: isPowerUser ? initialFilters.sort : 'name' });
    };

    const hidePlayerP6Ratings = hideMaskedP6
        ? [isRostersRolloutEnabled ? POWER6_KEYS.ACTIVE_ROSTER_MASKED : POWER6_KEYS.SCHOOL_MASKED]
        : [isRostersRolloutEnabled ? POWER6_KEYS.ACTIVE_ROSTER : POWER6_KEYS.SCHOOL];

    const customText = 'There are no results that meet this criteria.';
    return (
        <div className={`${styles.CollegeTeams} ${!isPowerUser ? styles.withNoPowerUser : ''}`}>
            <CollegeCornerFilters
                tempFilters={tempFilters}
                defaultFilters={filters}
                onhandleUpdateFilters={onhandleUpdateFilters}
                onhandleFilterSubmit={onhandleFilterSubmit}
                onhandleResetFilters={onhandleResetFilters}
                filterOptions={['gender', 'conference', 'division', 'location']}
                sortingOptions={columns
                    .filter((v) => {
                        if (v.title === 'P6' && !isPowerUser) {
                            v.sortable = false;
                        }
                        return v.sortable !== false;
                    }).map((v) => ({ label: v.title, value: v.key }))}
            />
            <div className={`${styles.dividerWhite}`} />
            <Table
                data-testid="college-corner.college-teams.table"
                sortRuleChanger={onhandleSort}
                columns={columns.map((v) => {
                    v.hideColumn = hidePlayerP6Ratings.includes(v.key);
                    if ((v.title === 'P6' || v.title === '#') && !isPowerUser) {
                        v.hideColumn = true;
                    }
                    return v;
                })}
                data={data}
                filters={filters}
                asyncCall={onAPILoad}
                onhandleClickLoadMore={onLoadMore}
                customNoRowsText={customText}
                mergeData
            />
        </div>
    );
}

LegacyCollegeTeams.propTypes = {
    history: PropTypes.instanceOf(Object).isRequired,
    location: PropTypes.instanceOf(Object).isRequired,
    isPowerUser: PropTypes.bool,
    hideMaskedP6: PropTypes.bool,
    isRostersRolloutEnabled: PropTypes.bool,
};

LegacyCollegeTeams.defaultProps = {
    isPowerUser: false,
    hideMaskedP6: false,
    isRostersRolloutEnabled: false,
};

function CollegeCornerCollegeTeams(props) {
    const { isEligible, viewerGender } = useCppCollegeTeamsAccess();

    if (isEligible) {
        return (
            <CppCollegeTeams
                history={props.history}
                location={props.location}
                viewerGender={viewerGender}
            />
        );
    }

    return <LegacyCollegeTeams {...props} />;
}

CollegeCornerCollegeTeams.propTypes = {
    history: PropTypes.instanceOf(Object).isRequired,
    location: PropTypes.instanceOf(Object).isRequired,
};

export default CollegeCornerCollegeTeams;
